import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { map, tap, distinctUntilChanged } from 'rxjs/operators';

import ICalculationResult = ZoningAPI.ICalculationResult;
import Response = ZoningAPI.Response;

import { environment } from '../../../environments/environment';
import { PropertyRecord, PropertyRecordFactory } from '../models';
import { BaseEntityService } from './base-entity.service';
import { MatSnackBar } from '@angular/material/snack-bar';

const API_ENDPOINT = environment.ZONAR_API_URL + '/property-record';

@Injectable({
    providedIn: 'root'
})
export class PropertyRecordService extends BaseEntityService<PropertyRecord> {
    private readonly propertyRecordSubject = new BehaviorSubject<PropertyRecord>(null);
    public readonly propertyRecord = this.propertyRecordSubject.asObservable().pipe(distinctUntilChanged());

    private readonly propertyRecordListSubject = new BehaviorSubject<PropertyRecord[]>(null);
    public readonly propertyRecordList = this.propertyRecordListSubject.asObservable().pipe(distinctUntilChanged());

    public isLoading = new BehaviorSubject<boolean>(false);

    constructor(private readonly http: HttpClient, private readonly snackBar: MatSnackBar) {
        super(PropertyRecordFactory);
        this.propertyRecordListSubject.next([]);
    }

    public getPropertyRecordInfo(
        id: string,
        version?: number,
        zaId?: number,
        allAttributes: boolean = false,
        getCalculationStatistic: boolean = false
    ) {
        const params = {
            fromObject: {
                gridicsId: id,
                allUses: 'true',
                frontages: 'true',
                applicableTypologies: 'true',
                includeDefaultTypology: 'true',
                getZoningCode: 'true',
                getCustomFields: 'true',
                getINCReasons: 'true',
                getCRMetaData: 'true'
            }
        };

        if (Number.isInteger(version)) {
            params.fromObject['zcVersionId'] = String(version);
        }
        if (Number.isInteger(zaId)) {
            params.fromObject['zaId'] = String(zaId);
        }
        if (allAttributes) {
            params.fromObject['allAttributes'] = 'true';
        }
        if (getCalculationStatistic) {
            params.fromObject['getCalculationStatistic'] = 'true';
        }

        const httpParams = new HttpParams(params);

        this.http
            .get<Response>(API_ENDPOINT, { params: httpParams })
            .pipe(
                map(response => {
                    return this.initIfDataExists(response.data);
                })
            )
            .subscribe(response => {
                this.propertyRecordSubject.next(response !== null ? response : undefined);
            });
    }

    public getPropertyRecordList(field: string, showLastZoningCodeVersion?: boolean) {
        field = encodeURIComponent(field);
        return this.http
            .get<Response>(`${API_ENDPOINT}/search/${field}?showLastZoningCodeVersion=${!!showLastZoningCodeVersion}`)
            .pipe(
                map(response => this.initEntities(response.data)),
                tap(response => {
                    this.propertyRecordListSubject.next(response);
                })
            );
    }

    public getPropertyRecordVersions(propertyRecordId: number, options: any) {
        const params = {
            fromObject: {
                recalculateLastResult: String(!!options.needToRecalculateLastResult),
                showResultsCalculatedWithParams: String(!!options.showResultsCalculatedWithParams)
            }
        };
        const httpParams = new HttpParams(params);
        return this.http
            .get<Response>(`${API_ENDPOINT}/${propertyRecordId}/version`, { params: httpParams })
            .pipe(map(response => response.data as ICalculationResult[]))
            .subscribe(response => {
                const propertyRecord = this.propertyRecordListSubject.value.find(pr => pr.Id === propertyRecordId);
                propertyRecord.CalculationResults = response;
                this.propertyRecordListSubject.next(this.propertyRecordListSubject.value);
                this.isLoading.next(false);
            });
    }

    public tryForcedCalculate(search: string) {
        return this.http
            .post<Response>(`${environment.ZONAR_API_URL}/forced-search/property-record`, { search })
            .pipe(
                tap(response => {
                    if (response.data === null) {
                        this.snackBar.open(
                            'Cannot find and calculate Property Record. Please check whether the correct Instance is selected.',
                            'OK',
                            {
                                verticalPosition: 'top',
                                horizontalPosition: 'center',
                                duration: 3000
                            }
                        );
                    }
                    return response;
                }),
                map(response => this.initIfDataExists(response.data)),
                tap(pr => (pr ? this.propertyRecordListSubject.next([pr]) : this.propertyRecordListSubject.next([])))
            );
    }
}
